import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Mail, Lock, User, Building2, Loader2, Sparkles } from "lucide-react";

export default function Login() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("modo") === "cadastro" ? "cadastro" : "login");
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (tab === "login") await login(form.email, form.password);
      else await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Falha na autenticação");
    } finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/* background mesh */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-cyan-500/20 blur-[120px]" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-blue-600/20 blur-[120px]" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl flex-col items-center justify-center gap-10 px-5 py-10 lg:flex-row lg:justify-between">
        {/* Left hero */}
        <div className="max-w-md animate-flow-in text-center lg:text-left">
          <div className="mb-6 inline-flex items-center gap-2.5">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-cyan-400 text-slate-950 cyan-glow">
              <Zap className="h-6 w-6" strokeWidth={2.6} />
            </div>
            <span className="font-display text-3xl font-extrabold tracking-tight text-white">Flow<span className="text-cyan-400">IA</span></span>
          </div>
          <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-3 py-1 text-xs font-mono-flow uppercase tracking-widest text-cyan-300">
            <Sparkles className="h-3.5 w-3.5" /> Assistente IA Empresarial
          </p>
          <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
            Atendimento no WhatsApp que <span className="text-cyan-400 text-glow">responde por você</span>.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-slate-400">
            IA humanizada, transcrição de voz, agenda com lembretes automáticos, CRM e relatórios diários — tudo em uma plataforma futurista.
          </p>
          <Link to="/planos" className="mt-6 inline-block text-sm font-medium text-cyan-400 hover:underline" data-testid="login-see-plans">
            Ver planos e preços →
          </Link>
        </div>

        {/* Right form card */}
        <div className="w-full max-w-md animate-flow-in">
          <div className="glow-card rounded-2xl p-6 sm:p-8">
            <div className="mb-6 grid grid-cols-2 gap-1 rounded-xl bg-slate-900/80 p-1">
              {["login", "cadastro"].map((t) => (
                <button
                  key={t}
                  data-testid="auth-toggle-tab"
                  onClick={() => { setTab(t); setError(""); }}
                  className={`rounded-lg py-2 text-sm font-semibold capitalize transition-all ${
                    tab === t ? "bg-cyan-400 text-slate-950" : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {t === "login" ? "Entrar" : "Cadastrar"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-4">
              {tab === "cadastro" && (
                <>
                  <Field icon={User} label="Seu nome" value={form.name} onChange={set("name")} placeholder="João Silva" testid="auth-name-input" />
                  <Field icon={Building2} label="Nome da empresa" value={form.company_name} onChange={set("company_name")} placeholder="Minha Empresa Ltda" testid="auth-company-input" />
                </>
              )}
              <Field icon={Mail} label="E-mail" type="email" value={form.email} onChange={set("email")} placeholder="voce@empresa.com" testid="auth-email-input" />
              <Field icon={Lock} label="Senha" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" testid="auth-password-input" />

              {error && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="auth-error">{error}</p>}

              <Button type="submit" disabled={loading} data-testid="auth-submit-button"
                className="h-11 w-full bg-cyan-400 text-base font-semibold text-slate-950 hover:bg-cyan-300">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : tab === "login" ? "Entrar" : "Criar conta"}
              </Button>
            </form>

            <div className="my-5 flex items-center gap-3 text-xs text-slate-500">
              <div className="h-px flex-1 bg-slate-800" /> ou <div className="h-px flex-1 bg-slate-800" />
            </div>

            <Button variant="outline" onClick={google} data-testid="auth-google-button"
              className="h-11 w-full border-slate-700 bg-slate-900/60 text-slate-100 hover:bg-slate-800">
              <img alt="Google" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="mr-2 h-5 w-5" />
              Continuar com Google
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, testid, type = "text", ...props }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-300">{label}</Label>
      <div className="relative">
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <Input type={type} data-testid={testid} className="h-11 border-slate-700 bg-slate-900/60 pl-10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400" required {...props} />
      </div>
    </div>
  );
}
